import { useState, useCallback, useEffect, useRef } from 'react';
export function createCallStrategy(config) {
    const { adapter } = config;
    return {
        useCall(context, item, props) {
            const [callState, setCallState] = useState(() => {
                const connectingCall = {
                    status: 'connecting',
                    createdAt: new Date(),
                    error: null,
                };
                return connectingCall;
            });
            const [userSpeaking, setUserSpeaking] = useState(false);
            const [systemSpeaking, setSystemSpeaking] = useState(false);
            const hasAutoStartedRef = useRef(false);
            const isCleaningUpRef = useRef(false);
            const isMountedRef = useRef(false);
            // Set up adapter event handlers
            useEffect(() => {
                isMountedRef.current = true;
                adapter.onConnectionStatusChanged((status) => {
                    // Don't update state during cleanup to prevent infinite loops
                    if (!isCleaningUpRef.current) {
                        setCallState((prev) => ({ ...prev, status }));
                    }
                });
                adapter.onUserSpeaking(() => {
                    setUserSpeaking(true);
                    setSystemSpeaking(false);
                    props.onUserSpeaking();
                });
                adapter.onUserStoppedSpeaking(() => {
                    setUserSpeaking(false);
                });
                adapter.onSystemSpeaking(() => {
                    setSystemSpeaking(true);
                    setUserSpeaking(false);
                    props.onSystemSpeaking();
                });
                adapter.onSystemStoppedSpeaking(() => {
                    setSystemSpeaking(false);
                });
                adapter.onError((error) => {
                    // Don't update state during cleanup to prevent infinite loops
                    if (!isCleaningUpRef.current) {
                        setCallState((prev) => ({
                            ...prev,
                            status: 'error',
                            error: {
                                message: error.message,
                                code: 'ADAPTER_ERROR',
                            },
                        }));
                    }
                    props.onError(error);
                });
                // Don't destroy static adapter on cleanup since it's shared
                // Only clean up if this is a non-static adapter
                return () => {
                    // No cleanup needed for static adapters
                };
            }, [adapter, props]);
            const connect = useCallback(async () => {
                try {
                    // Pass ApplicationContext to adapter for session creation
                    const instructions = config.getInstructions(item);
                    // console.log('instructions', instructions);
                    await adapter.connect({
                        token: context.token,
                        instructions,
                        context: item.context,
                        options: props.options, // Pass the runtime options (including TURN credentials)
                        onEnd(result) {
                            return props.onEnd({
                                id: `call-${Date.now()}`,
                                transcript: result.transcript,
                                summary: result.summary,
                                // actionItems: result.actionItems,
                                durationMs: Date.now() - callState.createdAt.getTime(),
                                createdAt: callState.createdAt,
                            });
                        },
                    });
                    await props.onConnect();
                }
                catch (error) {
                    await props.onError(error instanceof Error ? error : new Error('Connection failed'));
                }
            }, [adapter, props, context, callState]);
            const end = useCallback(async () => {
                // Send end event and wait for transcript response
                // const result = await adapter.sendEndEvent();
                const result = await adapter.end();
                const endedCall = {
                    id: `call-${Date.now()}`,
                    transcript: result.transcript,
                    summary: result.summary,
                    // actionItems: result.actionItems,
                    durationMs: Date.now() - callState.createdAt.getTime(),
                    createdAt: callState.createdAt,
                };
                setCallState({
                    status: 'ended',
                    createdAt: callState.createdAt,
                    error: null,
                });
                setUserSpeaking(false);
                setSystemSpeaking(false);
                await props.onEnd(endedCall);
                await adapter.disconnect();
                return endedCall;
            }, [adapter, callState, props]);
            // Auto-start call if enabled
            useEffect(() => {
                isCleaningUpRef.current = false;
                if (config.autoStart && !hasAutoStartedRef.current) {
                    console.log('Auto-starting call...');
                    hasAutoStartedRef.current = true;
                    connect().catch((error) => {
                        console.error('Auto-start failed:', error);
                        hasAutoStartedRef.current = false;
                    });
                }
                return () => {
                    isMountedRef.current = false;
                    isCleaningUpRef.current = true;
                    // Delay cleanup to handle Strict Mode unmount/remount
                    setTimeout(() => {
                        if (!isMountedRef.current) {
                            adapter.disconnect().catch((error) => {
                                console.error('Failed to disconnect adapter on cleanup:', error);
                            });
                        }
                        else {
                            isCleaningUpRef.current = false;
                        }
                    }, 0); // Use 0 timeout to run after potential remount
                };
            }, [config.autoStart, connect, adapter]); // Removed call.status to prevent re-runs on status changes
            const mute = useCallback(() => {
                adapter.mute();
            }, [adapter]);
            const unmute = useCallback(() => {
                adapter.unmute();
            }, [adapter]);
            const interrupt = useCallback(() => {
                adapter.interrupt();
            }, [adapter]);
            return {
                item: callState,
                userSpeaking,
                systemSpeaking,
                connect,
                end,
                mute,
                unmute,
                interrupt,
                get muted() {
                    return adapter.muted;
                },
            };
        },
    };
}
//# sourceMappingURL=callProvider.js.map